import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ShellHeader from '../components/ShellHeader';
import { useVehicles } from '../context/VehiclesContext';

function AboutUsPage({ authUser }) {
  const navigate = useNavigate();
  const { vehicles } = useVehicles();
  const [search, setSearch] = useState({
    make: '',
    model: '',
  });

  const sourceVehicles = useMemo(() => {
    const unsoldVehicles = vehicles.filter((vehicle) => !vehicle.isSold);
    return unsoldVehicles.length > 0 ? unsoldVehicles : vehicles;
  }, [vehicles]);

  const availableMakes = useMemo(() => {
    return [...new Set(sourceVehicles.map((vehicle) => vehicle.make).filter(Boolean))]
      .sort((left, right) => left.localeCompare(right));
  }, [sourceVehicles]);

  const availableModels = useMemo(() => {
    if (!search.make) {
      return [];
    }

    return [
      ...new Set(
        sourceVehicles
          .filter((vehicle) => vehicle.make === search.make)
          .map((vehicle) => vehicle.model)
          .filter(Boolean)
      ),
    ].sort((left, right) => left.localeCompare(right));
  }, [search.make, sourceVehicles]);

  const handleSearchChange = (event) => {
    const { name, value } = event.target;

    setSearch((current) => {
      if (name === 'make' && value !== current.make) {
        return {
          ...current,
          make: value,
          model: '',
        };
      }

      return {
        ...current,
        [name]: value,
      };
    });
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();

    const params = new URLSearchParams();

    if (search.make) {
      params.set('make', search.make);
    }

    if (search.model) {
      params.set('model', search.model);
    }

    navigate(`/search${params.toString() ? `?${params.toString()}` : ''}`);
  };

  return (
    <div className="site-shell">
      <ShellHeader authUser={authUser} />

      <main className="content-wrap about-content-wrap">
        <section className="content-page-block about-page-main">
          <div className="section-header">
            <h2>About M.J. Motors</h2>
          </div>

          <p>
            M.J. Motors is a family run business that goes the extra mile to give an outstanding
            service to each individual customer. Established for over 30 years, we have built our
            reputation on honest advice, quality preparation, and long term customer relationships.
          </p>

          <p>
            We operate from two sites within the Tunstall area and serve drivers across Stoke-on-Trent,
            Staffordshire, and surrounding locations. Our team supplies a large range of quality used cars
            and vans, including both petrol and diesel models, with stock chosen for reliability,
            value, and everyday practicality.
          </p>

          <p>
            Whether you are buying your first vehicle, upgrading the family car, or looking for a dependable
            used van for work, we focus on making the process straightforward from start to finish. We offer
            finance with strong acceptance rates, and we work hard to find options that fit different budgets
            and circumstances.
          </p>

          <p>
            If you are searching for trusted used car dealers in Tunstall with friendly local support,
            M.J. Motors is here to help. Browse our latest stock online or contact us directly for tailored
            guidance and current availability.
          </p>
        </section>

        <aside className="about-page-sidebar" aria-label="About page tools">
          <section className="content-page-block about-side-panel">
            <h3 className="about-side-title">Search Stock</h3>
            <form className="search-panel about-search-panel" onSubmit={handleSearchSubmit}>
              <label className="search-field">
                <span>Make</span>
                <select name="make" value={search.make} onChange={handleSearchChange}>
                  <option value="">All makes</option>
                  {availableMakes.map((make) => (
                    <option key={make} value={make}>
                      {make}
                    </option>
                  ))}
                </select>
              </label>

              <label className="search-field">
                <span>Model</span>
                <select
                  name="model"
                  value={search.model}
                  onChange={handleSearchChange}
                  disabled={!search.make}
                >
                  <option value="">All models</option>
                  {availableModels.map((model) => (
                    <option key={model} value={model}>
                      {model}
                    </option>
                  ))}
                </select>
              </label>

              <button type="submit" className="search-submit-btn">Search Stock</button>
            </form>
          </section>

          <section className="content-page-block about-side-panel">
            <h3 className="about-side-title">Contact Us</h3>
            <p className="about-contact-text">
              Speak with our team about stock, finance options, or part exchange.
            </p>
            <a className="about-contact-link" href="tel:01782835561">01782 835561</a>
            <a className="about-contact-link" href="mailto:markjeffery1000@yahoo.co.uk">
              markjeffery1000@yahoo.co.uk
            </a>
          </section>
        </aside>
      </main>
    </div>
  );
}

export default AboutUsPage;
